import type { IntegerAmount } from '@actual-app/core/shared/util';

import {
  gastoPorCategoria,
  ingresosGastosPorMes,
} from '#components/mobile/informes/calculos';
import type {
  Categorias,
  Movimiento,
} from '#components/mobile/informes/calculos';
import {
  recortarInicioSinDatos,
  referenciaHabitual,
} from '#components/mobile/informes/estadisticaRobusta';

/**
 * «Diario de a bordo»: el informe de la semana (de lunes a domingo) y el del
 * mes (natural o de nómina a nómina). Cálculo puro, sin React. Todo en
 * céntimos; las fechas son textos 'yyyy-MM-dd'.
 *
 * Reutiliza los cálculos de `calculos.ts` poniendo en `mes` la clave del
 * periodo (la fecha de su primer día) en vez del mes.
 */

/** Un movimiento agrupado por día × categoría × beneficiario. */
export type MovimientoDia = {
  fecha: string; // 'yyyy-MM-dd'
  categoria: string | null;
  payee: string | null;
  importe: IntegerAmount;
};

export type Periodo = {
  /** Primer día (también es la clave del periodo). */
  desde: string;
  /** Último día, incluido. */
  hasta: string;
};

/** Día de cobro: 1..31 (se recorta al último día del mes) o el último. */
export type DiaCorte = number | 'ultimo';

// ---------------------------------------------------------------------------
// Fechas
// ---------------------------------------------------------------------------

function aUTC(fecha: string): number {
  const [a, m, d] = fecha.split('-').map(Number);
  return Date.UTC(a, m - 1, d);
}

function deUTC(ms: number): string {
  const f = new Date(ms);
  return `${f.getUTCFullYear()}-${String(f.getUTCMonth() + 1).padStart(2, '0')}-${String(f.getUTCDate()).padStart(2, '0')}`;
}

export function sumarDias(fecha: string, n: number): string {
  return deUTC(aUTC(fecha) + n * 86400000);
}

export function diasEntre(desde: string, hasta: string): number {
  return Math.round((aUTC(hasta) - aUTC(desde)) / 86400000);
}

/** Día de la semana con el lunes = 0. */
export function diaDeLaSemana(fecha: string): number {
  return (new Date(aUTC(fecha)).getUTCDay() + 6) % 7;
}

function diasDelMes(mes: string): number {
  const [a, m] = mes.split('-').map(Number);
  return new Date(Date.UTC(a, m, 0)).getUTCDate();
}

function mesSiguiente(mes: string): string {
  const [a, m] = mes.split('-').map(Number);
  return m === 12 ? `${a + 1}-01` : `${a}-${String(m + 1).padStart(2, '0')}`;
}

function mesAnterior(mes: string): string {
  const [a, m] = mes.split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
}

/** Días de un periodo, del primero al último. */
export function diasDe(p: Periodo): string[] {
  const n = diasEntre(p.desde, p.hasta) + 1;
  return Array.from({ length: Math.max(n, 0) }, (_, i) =>
    sumarDias(p.desde, i),
  );
}

// ---------------------------------------------------------------------------
// Periodos
// ---------------------------------------------------------------------------

/** Semana de lunes a domingo que contiene `fecha`. */
export function semanaDe(fecha: string): Periodo {
  const desde = sumarDias(fecha, -diaDeLaSemana(fecha));
  return { desde, hasta: sumarDias(desde, 6) };
}

/** Las `n` semanas que acaban en la que empieza en `lunes`, en orden. */
export function semanasHasta(lunes: string, n: number): Periodo[] {
  return Array.from({ length: n }, (_, i) =>
    semanaDe(sumarDias(lunes, -7 * (n - 1 - i))),
  );
}

/** Día en que empieza el periodo de nómina de `mes` ('yyyy-MM'). */
function diaDeCorte(mes: string, corte: DiaCorte): string {
  const ultimo = diasDelMes(mes);
  const dia =
    corte === 'ultimo' ? ultimo : Math.min(Math.max(corte, 1), ultimo);
  return `${mes}-${String(dia).padStart(2, '0')}`;
}

/**
 * Periodo «del mes» `mes`. Sin corte, el mes natural. Con corte, de nómina a
 * nómina: del día de cobro de `mes` al día anterior al cobro del mes
 * siguiente (el periodo lleva el nombre del mes en que se cobra).
 */
export function periodoMes(mes: string, corte: DiaCorte | null): Periodo {
  if (corte == null) {
    return {
      desde: `${mes}-01`,
      hasta: `${mes}-${String(diasDelMes(mes)).padStart(2, '0')}`,
    };
  }
  return {
    desde: diaDeCorte(mes, corte),
    hasta: sumarDias(diaDeCorte(mesSiguiente(mes), corte), -1),
  };
}

/** Mes ('yyyy-MM') cuyo periodo contiene `fecha`. */
export function mesDeFecha(fecha: string, corte: DiaCorte | null): string {
  const mes = fecha.slice(0, 7);
  if (corte == null) {
    return mes;
  }
  return fecha >= diaDeCorte(mes, corte) ? mes : mesAnterior(mes);
}

/** Los `n` meses que acaban en `mes`, en orden. */
export function mesesHasta(mes: string, n: number): string[] {
  const res = [mes];
  while (res.length < n) {
    res.unshift(mesAnterior(res[0]));
  }
  return res;
}

/** El último periodo cerrado (que acabó antes de `hoy`). */
export function ultimoMesCerrado(hoy: string, corte: DiaCorte | null): string {
  return mesAnterior(mesDeFecha(hoy, corte));
}

export function ultimaSemanaCerrada(hoy: string): Periodo {
  return semanaDe(sumarDias(semanaDe(hoy).desde, -1));
}

/**
 * Movimientos con el periodo en `mes` (la fecha del primer día), para los
 * cálculos de `calculos.ts`. Los que no caen en ningún periodo se quitan.
 */
export function porPeriodos(
  movimientos: readonly MovimientoDia[],
  periodos: readonly Periodo[],
): Movimiento[] {
  const res: Movimiento[] = [];
  for (const m of movimientos) {
    const p = periodos.find(x => m.fecha >= x.desde && m.fecha <= x.hasta);
    if (p) {
      res.push({
        mes: p.desde,
        categoria: m.categoria,
        payee: m.payee,
        importe: m.importe,
      });
    }
  }
  return res;
}

// ---------------------------------------------------------------------------
// Resumen de un periodo
// ---------------------------------------------------------------------------

export type FilaCategoria = {
  categoria: string | null;
  /** Gasto del periodo, en positivo. */
  importe: IntegerAmount;
  /** Lo habitual (mediana sin atípicos de los periodos previos); null sin historia. */
  habitual: IntegerAmount | null;
  /** importe − habitual (positivo = más que de costumbre). */
  diferencia: IntegerAmount | null;
};

export type ResumenDiario = {
  ingresos: IntegerAmount;
  gastos: IntegerAmount;
  ahorro: IntegerAmount;
  tasa: number | null;
  /** Gastos del periodo anterior (para «vs agosto»); null si no hay. */
  gastosAnterior: IntegerAmount | null;
  /**
   * Cuántos periodos atrás hay que ir para encontrar uno con más ahorro
   * («tu mejor mes desde junio»). null si nunca: es el mejor de los vistos.
   * 1 = el anterior ya fue mejor.
   */
  mejorDesde: number | null;
  /** Gasto habitual de un periodo (mediana de los previos); null sin historia. */
  gastoHabitual: IntegerAmount | null;
  categorias: FilaCategoria[];
  /** Gasto de cada día del periodo, en positivo, alineado con `diasDe`. */
  porDia: IntegerAmount[];
  diasSinGastar: number;
  /** Días del periodo que ya han pasado (hasta hoy incluido). */
  diasTranscurridos: number;
  enCurso: boolean;
};

/** Diferencia menor que esto (en céntimos) es «como siempre». */
export const MARGEN_COMO_SIEMPRE = 1000;

/**
 * Resumen de `periodos[periodos.length - 1]` frente a los anteriores (que
 * hacen de historia). `hoy` marca si el periodo está en curso.
 */
export function resumenPeriodo(
  movimientos: readonly MovimientoDia[],
  periodos: readonly Periodo[],
  categorias: Categorias,
  hoy: string,
): ResumenDiario {
  const actual = periodos[periodos.length - 1];
  const claves = periodos.map(p => p.desde);
  const movs = porPeriodos(movimientos, periodos);

  const ig = ingresosGastosPorMes(movs, claves, categorias);
  const este = ig[ig.length - 1];
  const previos = ig.slice(0, -1);
  // Solo cuenta la historia desde el primer periodo con algún movimiento.
  const primeroConDatos = previos.findIndex(
    p => p.ingresos !== 0 || p.gastos !== 0,
  );
  const historia = primeroConDatos < 0 ? [] : previos.slice(primeroConDatos);
  const anterior = historia[historia.length - 1] ?? null;

  let mejorDesde: number | null = null;
  for (let i = historia.length - 1; i >= 0; i--) {
    if (historia[i].ahorro > este.ahorro) {
      mejorDesde = historia.length - i;
      break;
    }
  }

  const gastoHabitual =
    historia.length > 0
      ? referenciaHabitual(historia.map(h => h.gastos)).referencia
      : null;

  // Categorías: el periodo frente a lo habitual de cada una.
  const desglose = gastoPorCategoria(movs, [actual.desde], categorias);
  const clavesHistoria = historia.map(h => h.mes);
  const porCategoriaYPeriodo = new Map<string | null, Map<string, number>>();
  for (const m of movs) {
    if (!clavesHistoria.includes(m.mes)) {
      continue;
    }
    const mapa = porCategoriaYPeriodo.get(m.categoria) ?? new Map();
    mapa.set(m.mes, (mapa.get(m.mes) ?? 0) - m.importe);
    porCategoriaYPeriodo.set(m.categoria, mapa);
  }
  const filas: FilaCategoria[] = desglose.filas.map(f => {
    if (clavesHistoria.length === 0) {
      return { ...f, habitual: null, diferencia: null };
    }
    const mapa = porCategoriaYPeriodo.get(f.categoria);
    // Los periodos antes del primer gasto de la categoría no cuentan: si es
    // nueva no hay «habitual» con el que compararla.
    const serie = recortarInicioSinDatos(
      clavesHistoria.map(c => Math.max(mapa?.get(c) ?? 0, 0)),
    );
    if (serie.length === 0) {
      return { ...f, habitual: null, diferencia: null };
    }
    const habitual = referenciaHabitual(serie).referencia;
    return { ...f, habitual, diferencia: f.importe - habitual };
  });

  // Día a día: solo salidas de categorías de gasto (y sin categoría).
  const dias = diasDe(actual);
  const porDiaMapa = new Map<string, number>();
  for (const m of movimientos) {
    if (m.fecha < actual.desde || m.fecha > actual.hasta) {
      continue;
    }
    if (m.categoria != null && categorias.get(m.categoria)?.esIngreso) {
      continue;
    }
    if (m.importe < 0) {
      porDiaMapa.set(m.fecha, (porDiaMapa.get(m.fecha) ?? 0) - m.importe);
    }
  }
  const porDia = dias.map(d => porDiaMapa.get(d) ?? 0);
  const transcurridos = dias.filter(d => d <= hoy).length;
  const diasSinGastar = dias.filter(
    (d, i) => d <= hoy && porDia[i] === 0,
  ).length;

  return {
    ingresos: este.ingresos,
    gastos: este.gastos,
    ahorro: este.ahorro,
    tasa: este.tasa,
    gastosAnterior: anterior ? anterior.gastos : null,
    mejorDesde,
    gastoHabitual,
    categorias: filas,
    porDia,
    diasSinGastar,
    diasTranscurridos: transcurridos,
    enCurso: actual.hasta >= hoy,
  };
}

// ---------------------------------------------------------------------------
// El plan
// ---------------------------------------------------------------------------

export type ResumenPlan = {
  /** Categorías de gasto con algo asignado o gastado. */
  total: number;
  dentro: number;
  /** Las que gastaron más de lo asignado, de mayor a menor exceso. */
  pasadas: Array<{ categoria: string; exceso: IntegerAmount }>;
  asignado: IntegerAmount;
};

/**
 * Qué categorías se quedaron dentro de lo asignado ese mes. «Pasarse» es
 * gastar más de lo asignado en el mes (sin contar lo que sobró de antes).
 */
export function resumenPlan(
  gastoPorCategoriaDelMes: ReadonlyArray<{
    categoria: string | null;
    importe: IntegerAmount;
  }>,
  asignado: ReadonlyMap<string, IntegerAmount>,
  categorias: Categorias,
): ResumenPlan {
  const gasto = new Map<string, number>();
  for (const f of gastoPorCategoriaDelMes) {
    if (f.categoria != null) {
      gasto.set(f.categoria, f.importe);
    }
  }
  const ids = new Set<string>([...gasto.keys()]);
  for (const [id, importe] of asignado) {
    if (importe > 0) {
      ids.add(id);
    }
  }
  let dentro = 0;
  let total = 0;
  let totalAsignado = 0;
  const pasadas: ResumenPlan['pasadas'] = [];
  for (const id of ids) {
    const info = categorias.get(id);
    if (!info || info.esIngreso) {
      continue;
    }
    total++;
    const a = Math.max(asignado.get(id) ?? 0, 0);
    totalAsignado += a;
    const g = gasto.get(id) ?? 0;
    if (g <= a) {
      dentro++;
    } else {
      pasadas.push({ categoria: id, exceso: g - a });
    }
  }
  pasadas.sort((x, y) => y.exceso - x.exceso);
  return { total, dentro, pasadas, asignado: totalAsignado };
}

export type Ritmo = {
  /** Fracción del plan del mes ya gastada (puede pasar de 1). */
  usado: number;
  /** Fracción del mes que ya ha pasado. */
  transcurrido: number;
  /** Lo que faltaría (+) o sobraría (−) a fin de mes al ritmo actual. */
  proyeccion: IntegerAmount;
};

/** Cómo va el plan del mes frente al calendario. null sin plan. */
export function ritmoDelMes(
  gastado: IntegerAmount,
  asignado: IntegerAmount,
  dia: number,
  diasMes: number,
): Ritmo | null {
  if (asignado <= 0 || diasMes <= 0) {
    return null;
  }
  const transcurrido = Math.min(Math.max(dia / diasMes, 0), 1);
  const previsto = transcurrido > 0 ? gastado / transcurrido : gastado;
  return {
    usado: gastado / asignado,
    transcurrido,
    proyeccion: Math.round(previsto - asignado),
  };
}

/** Gasto de un día normal: la séptima parte de una semana normal. */
export function diaNormal(gastoHabitualSemana: IntegerAmount | null) {
  return gastoHabitualSemana == null
    ? null
    : Math.round(gastoHabitualSemana / 7);
}

// ---------------------------------------------------------------------------
// Gavi
// ---------------------------------------------------------------------------

export type ClaveFraseGavi =
  | 'sin-datos'
  | 'mejor-de-todos'
  | 'mejor-desde'
  | 'en-rojo'
  | 'por-debajo'
  | 'por-encima'
  | 'como-siempre';

/**
 * Qué comenta Gavi del periodo. En el mes manda el ahorro; en la semana,
 * el gasto frente a una semana normal.
 */
export function claveFraseGavi(
  r: ResumenDiario,
  tipo: 'semana' | 'mes',
): ClaveFraseGavi {
  if (r.gastos === 0 && r.ingresos === 0) {
    return 'sin-datos';
  }
  if (tipo === 'mes' && !r.enCurso) {
    if (r.ahorro < 0) {
      return 'en-rojo';
    }
    if (r.ahorro > 0 && r.gastosAnterior != null) {
      if (r.mejorDesde == null) {
        return 'mejor-de-todos';
      }
      if (r.mejorDesde >= 3) {
        return 'mejor-desde';
      }
    }
  }
  if (r.gastoHabitual == null || r.gastoHabitual === 0) {
    return 'como-siempre';
  }
  const ref =
    r.enCurso && tipo === 'semana'
      ? (r.gastoHabitual * r.diasTranscurridos) / 7
      : r.gastoHabitual;
  const diferencia = r.gastos - ref;
  if (Math.abs(diferencia) < Math.max(MARGEN_COMO_SIEMPRE, ref * 0.05)) {
    return 'como-siempre';
  }
  return diferencia < 0 ? 'por-debajo' : 'por-encima';
}

/** La categoría que más se desvía hacia arriba (para que Gavi la nombre). */
export function categoriaQueSube(r: ResumenDiario): FilaCategoria | null {
  const candidatas = r.categorias.filter(
    f => f.diferencia != null && f.diferencia >= MARGEN_COMO_SIEMPRE,
  );
  candidatas.sort((a, b) => (b.diferencia ?? 0) - (a.diferencia ?? 0));
  return candidatas[0] ?? null;
}
